import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { adminOrders, Order, SelectedOption, BulkOrderStatusResponse } from '../../api/admin';
import { FiEye, FiCheck, FiX, FiClock, FiPackage, FiTruck, FiDollarSign } from 'react-icons/fi';
import { formatCurrency } from '../../utils/currency';
import { getStatusLabel } from '../../utils/orderStatus';
import { resolveUploadUrl } from '../../utils/imageUrl';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 16px;

  @media (max-width: 768px) {
    padding: 0 12px;
  }
`;

const Header = styled.div`
  margin-bottom: 24px;
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #1C1511;
  margin-bottom: 16px;
  font-family: Georgia, serif;

  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const Filters = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  overflow-x: auto;
  padding-bottom: 8px;

  @media (max-width: 768px) {
    gap: 8px;
  }
`;

const FilterButton = styled.button<{ active?: boolean }>`
  padding: 8px 16px;
  background: ${props => props.active ? '#D4A017' : '#F5F0E8'};
  color: ${props => props.active ? '#1C1511' : '#6B5E4E'};
  border: 1px solid ${props => props.active ? '#D4A017' : '#E8DDD0'};
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  @media (max-width: 768px) {
    padding: 6px 12px;
    font-size: 13px;
  }
`;

// Desktop Table Layout
const TableContainer = styled.div`
  @media (max-width: 768px) {
    display: none;
  }
`;

const Table = styled.table`
  width: 100%;
  background: #F5F0E8;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const Thead = styled.thead`
  background: #1C1511;
`;

const Th = styled.th`
  padding: 12px 16px;
  text-align: left;
  font-size: 12px;
  font-weight: 600;
  color: #FFFFFF;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  border-bottom: 1px solid #E8DDD0;

  &:hover {
    background: #ede8df;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const Td = styled.td`
  padding: 16px;
  font-size: 14px;
  color: #1C1511;
`;

// Mobile Card Layout
const MobileContainer = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: block;
  }
`;

const OrderCard = styled.div`
  background: #F5F0E8;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
`;

const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CardRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CardLabel = styled.span`
  font-size: 12px;
  color: #6B5E4E;
  font-weight: 500;
`;

const CardValue = styled.span`
  font-size: 14px;
  color: #1C1511;
  font-weight: 500;
`;

const OrderNumber = styled.div`
  font-weight: 600;
  color: #1C1511;
  margin-bottom: 4px;
`;

const OrderDate = styled.div`
  font-size: 12px;
  color: #6B5E4E;
`;

const CustomerInfo = styled.div``;

const CustomerName = styled.div`
  font-weight: 500;
  color: #1C1511;
  margin-bottom: 2px;
`;

const CustomerContact = styled.div`
  font-size: 12px;
  color: #6B5E4E;
`;

const StatusBadge = styled.span<{ status: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => {
    switch (props.status.toLowerCase()) {
      case 'pending': return '#fef3c7';
      case 'confirmed': return '#dbeafe';
      case 'in_preparation': return '#e0e7ff';
      case 'ready_for_pickup': return '#d1fae5';
      case 'completed': return '#d1fae5';
      case 'cancelled': return '#fee2e2';
      default: return '#f3f4f6';
    }
  }};
  color: ${props => {
    switch (props.status.toLowerCase()) {
      case 'pending': return '#92400e';
      case 'confirmed': return '#1e3a8a';
      case 'in_preparation': return '#3730a3';
      case 'ready_for_pickup': return '#065f46';
      case 'completed': return '#065f46';
      case 'cancelled': return '#991b1b';
      default: return '#6b7280';
    }
  }};
`;

const PaymentBadge = styled.span<{ status: string }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => {
    switch(props.status?.toLowerCase()) {
      case 'completed':
      case 'paid':
        return '#d1fae5';
      case 'processing':
        return '#fef3c7';
      case 'failed':
        return '#fee2e2';
      case 'refunded':
        return '#e0e7ff';
      default:
        return '#f3f4f6';
    }
  }};
  color: ${props => {
    switch(props.status?.toLowerCase()) {
      case 'completed':
      case 'paid':
        return '#065f46';
      case 'processing':
        return '#92400e';
      case 'failed':
        return '#991b1b';
      case 'refunded':
        return '#3730a3';
      default:
        return '#4b5563';
    }
  }};
`;

const Amount = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #1C1511;
`;

const PickupInfo = styled.div``;

const PickupDate = styled.div`
  font-weight: 500;
  color: #1C1511;
  margin-bottom: 2px;
`;

const PickupTime = styled.div`
  font-size: 12px;
  color: #6B5E4E;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button`
  padding: 6px 10px;
  background: #D4A017;
  color: #1C1511;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 4px;

  &:hover {
    background: #c49015;
  }
`;

const QuickActionButton = styled(ActionButton)<{ variant: 'complete' | 'cancel' }>`
  background: ${props => props.variant === 'complete' ? '#16a34a' : '#dc2626'};
  color: #FFFFFF;

  &:hover:not(:disabled) {
    background: ${props => props.variant === 'complete' ? '#15803d' : '#b91c1c'};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ActionError = styled.div`
  margin-top: 12px;
  padding: 10px 14px;
  background: #fee2e2;
  color: #991b1b;
  border-radius: 8px;
  font-size: 14px;
`;

const BulkBar = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
  padding: 12px 16px;
  background: #F5F0E8;
  border: 1px solid #D4A017;
  border-radius: 8px;
`;

const BulkCount = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #1C1511;
`;

const BulkResult = styled.div`
  margin-bottom: 16px;
  padding: 12px 16px;
  background: #F5F0E8;
  border: 1px solid #E8DDD0;
  border-radius: 8px;
  font-size: 14px;
  color: #1C1511;

  ul {
    margin: 8px 0 0;
    padding-left: 20px;
  }
`;

const SelectAllRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  font-size: 14px;
  color: #1C1511;
`;

const SelectCell = styled.span`
  display: inline-flex;
  align-items: center;
  margin-right: 8px;
`;

const Modal = styled.div<{ isOpen: boolean }>`
  display: ${props => props.isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  align-items: center;
  justify-content: center;
  z-index: 2000;
`;

const ModalContent = styled.div`
  background: #FAF8F3;
  border-radius: 12px;
  max-width: 600px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  padding: 24px;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const ModalTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #1C1511;
  font-family: Georgia, serif;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: #6B5E4E;
  cursor: pointer;

  &:hover {
    color: #1C1511;
  }
`;

const OrderDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const DetailSection = styled.div`
  border-bottom: 1px solid #E8DDD0;
  padding-bottom: 16px;

  &:last-child {
    border-bottom: none;
  }
`;

const DetailTitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  color: #6B5E4E;
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 14px;
`;

const DetailLabel = styled.span`
  color: #6B5E4E;
`;

const DetailValue = styled.span`
  color: #1C1511;
  font-weight: 500;
`;

const ItemsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Item = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 8px;
  background: #F5F0E8;
  border-radius: 6px;
`;

const StatusSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid #E8DDD0;
  border-radius: 6px;
  font-size: 14px;
  margin-right: 8px;
  background: #FFFFFF;
  color: #1C1511;

  &:focus {
    outline: none;
    border-color: #D4A017;
    box-shadow: 0 0 0 3px rgba(212, 160, 23, 0.1);
  }
`;

const UpdateButton = styled.button`
  padding: 8px 16px;
  background: #D4A017;
  color: #1C1511;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(212, 160, 23, 0.3);
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 400px;
`;

const Spinner = styled.div`
  border: 3px solid #E8DDD0;
  border-top: 3px solid #D4A017;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #6B5E4E;
  background: #F5F0E8;
  border-radius: 12px;
`;

// Renders a customizable product's selected options as "Category: Item, ..."
// text, matching AdminQBOrders' muted key:value convention. Returns '' when
// there are no selections so callers can skip rendering entirely.
const formatSelectedOptions = (options: SelectedOption[] | null | undefined, t: (key: string) => string): string => {
  if (!options || options.length === 0) return '';
  return options.map(opt => `${opt.category_name ?? t('admin.orders.optionFallbackLabel')}: ${opt.item_name}`).join(', ');
};

const formatPaymentStatus = (status: string | undefined, t: (key: string) => string) => {
  if (!status) return t('admin.orders.paymentStatusPending');
  switch (status.toLowerCase()) {
    case 'completed': return t('admin.orders.paymentStatusCompleted');
    case 'paid': return t('admin.orders.paymentStatusPaid');
    case 'processing': return t('admin.orders.paymentStatusProcessing');
    case 'failed': return t('admin.orders.paymentStatusFailed');
    case 'refunded': return t('admin.orders.paymentStatusRefunded');
    case 'pending': return t('admin.orders.paymentStatusPending');
    default: return status;
  }
};

// Mirrors the backend's maximum number of order ids accepted by one bulk status update request.
const BULK_MAX_IDS = 200;

const AdminOrders: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [filter, setFilterState] = useState<string>('all');
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [newStatus, setNewStatus] = useState<string>('');
  const [pendingOrderIds, setPendingOrderIds] = useState<Set<number>>(new Set());
  const [actionError, setActionError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkPending, setBulkPending] = useState(false);
  const [bulkResult, setBulkResult] = useState<BulkOrderStatusResponse | null>(null);

  const setFilter = (next: string) => {
    setFilterState(next);
    setSelectedIds(new Set());
    setBulkResult(null);
  };

  const { data: rawOrders, isLoading } = useQuery({
    queryKey: ['adminOrders', filter],
    queryFn: () => adminOrders.getAll(filter === 'all' ? {} : { status: filter }),
  });
  const orders = Array.isArray(rawOrders) ? rawOrders : [];

  // Drop selections for orders that are no longer in the rendered list after a refetch.
  useEffect(() => {
    const list = Array.isArray(rawOrders) ? rawOrders : [];
    setSelectedIds(prev => {
      if (prev.size === 0) return prev;
      const present = new Set(list.map(o => o.order_id));
      const next = new Set(Array.from(prev).filter(id => present.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [rawOrders]);

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: number; status: string }) =>
      adminOrders.updateStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminOrders'] });
      setSelectedOrder(null);
    },
  });

  const handleViewDetails = async (order: Order) => {
    setActionError(null);
    const detailedOrder = await adminOrders.getById(order.order_id);
    setSelectedOrder(detailedOrder);
    setNewStatus(detailedOrder.status);
  };

  const handleUpdateStatus = () => {
    if (selectedOrder && newStatus !== selectedOrder.status) {
      updateStatusMutation.mutate({ id: selectedOrder.order_id, status: newStatus });
    }
  };

  const isTerminalStatus = (status: string) => ['completed', 'cancelled'].includes(status.toLowerCase());

  const handleQuickAction = async (order: Pick<Order, 'order_id' | 'order_number'>, status: 'completed' | 'cancelled') => {
    if (pendingOrderIds.has(order.order_id)) return;
    if (status === 'cancelled' && !window.confirm(t('admin.orders.cancelConfirm', { number: order.order_number }))) {
      return;
    }
    setActionError(null);
    setPendingOrderIds(prev => new Set(prev).add(order.order_id));
    try {
      await adminOrders.updateStatus(order.order_id, status);
      queryClient.invalidateQueries({ queryKey: ['adminOrders'] });
      setSelectedIds(prev => {
        if (!prev.has(order.order_id)) return prev;
        const next = new Set(prev);
        next.delete(order.order_id);
        return next;
      });
      setSelectedOrder(current => (current && current.order_id === order.order_id ? null : current));
    } catch (err: any) {
      setActionError(t('admin.orders.actionFailed'));
      const status = err?.response?.status;
      if (status === 400 || status === 404) {
        queryClient.invalidateQueries({ queryKey: ['adminOrders'] });
      }
    } finally {
      setPendingOrderIds(prev => {
        const next = new Set(prev);
        next.delete(order.order_id);
        return next;
      });
    }
  };

  const allSelected = orders.length > 0 && orders.every(o => selectedIds.has(o.order_id));
  const someSelected = selectedIds.size > 0 && !allSelected;

  const toggleOne = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelectedIds(allSelected ? new Set() : new Set(orders.map(o => o.order_id)));
  };

  const setIndeterminate = (el: HTMLInputElement | null) => {
    if (el) el.indeterminate = someSelected;
  };

  const handleBulkAction = async (status: 'completed' | 'cancelled') => {
    if (bulkPending || selectedIds.size === 0) return;
    const count = selectedIds.size;
    if (count > BULK_MAX_IDS) {
      setActionError(t('admin.orders.bulkTooManySelected', { count, max: BULK_MAX_IDS }));
      return;
    }
    let message = status === 'completed'
      ? t('admin.orders.bulkCompleteConfirm', { count })
      : t('admin.orders.bulkCancelConfirm', { count });
    if (status === 'cancelled') {
      const completedCount = orders.filter(
        o => selectedIds.has(o.order_id) && o.status.toLowerCase() === 'completed'
      ).length;
      if (completedCount > 0) {
        message += '\n\n' + t('admin.orders.bulkCancelCompletedWarning', { count: completedCount });
      }
    }
    if (!window.confirm(message)) return;
    setActionError(null);
    setBulkPending(true);
    try {
      const response = await adminOrders.bulkUpdateStatus(Array.from(selectedIds), status);
      setBulkResult(response);
      setSelectedIds(new Set());
      queryClient.invalidateQueries({ queryKey: ['adminOrders'] });
    } catch {
      setActionError(t('admin.orders.actionFailed'));
    } finally {
      setBulkPending(false);
    }
  };

  const bulkReasonLabel = (reason: string | null) => {
    switch (reason) {
      case 'cancelled': return t('admin.orders.bulkReasonCancelled');
      case 'already_completed': return t('admin.orders.bulkReasonAlreadyCompleted');
      case 'already_cancelled': return t('admin.orders.bulkReasonAlreadyCancelled');
      case 'not_found': return t('admin.orders.bulkReasonNotFound');
      case 'busy': return t('admin.orders.bulkReasonBusy');
      default: return t('admin.orders.bulkReasonError');
    }
  };

  const renderQuickActions = (order: Pick<Order, 'order_id' | 'order_number' | 'status'>, suffix: string | number, fullWidth = false) => {
    if (isTerminalStatus(order.status)) return null;
    const busy = pendingOrderIds.has(order.order_id);
    const style = fullWidth ? { flex: 1, justifyContent: 'center' } : undefined;
    return (
      <>
        <QuickActionButton
          variant="complete"
          style={style}
          data-testid={`order-complete-btn-${suffix}`}
          disabled={busy}
          aria-busy={busy}
          onClick={() => handleQuickAction(order, 'completed')}
        >
          <FiCheck />
          {t('admin.orders.markCompleted')}
        </QuickActionButton>
        <QuickActionButton
          variant="cancel"
          style={style}
          data-testid={`order-cancel-btn-${suffix}`}
          disabled={busy}
          aria-busy={busy}
          onClick={() => handleQuickAction(order, 'cancelled')}
        >
          <FiX />
          {t('admin.orders.cancelOrder')}
        </QuickActionButton>
      </>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatTime = (timeString: string) => {
    return timeString;
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending': return <FiClock />;
      case 'confirmed': return <FiCheck />;
      case 'in_preparation': return <FiPackage />;
      case 'ready_for_pickup': return <FiTruck />;
      case 'completed': return <FiCheck />;
      default: return null;
    }
  };

  if (isLoading) {
    return (
      <LoadingContainer>
        <Spinner />
      </LoadingContainer>
    );
  }

  return (
    <Container>
      <Header>
        <Title>{t('admin.orders.title')}</Title>
        <Filters>
          <FilterButton active={filter === 'all'} onClick={() => setFilter('all')}>
            {t('admin.orders.filterAll')}
          </FilterButton>
          <FilterButton active={filter === 'pending'} onClick={() => setFilter('pending')}>
            {t('admin.orders.filterPending')}
          </FilterButton>
          <FilterButton active={filter === 'confirmed'} onClick={() => setFilter('confirmed')}>
            {t('admin.orders.filterConfirmed')}
          </FilterButton>
          <FilterButton active={filter === 'in_preparation'} onClick={() => setFilter('in_preparation')}>
            {t('admin.orders.filterInPreparation')}
          </FilterButton>
          <FilterButton active={filter === 'ready'} onClick={() => setFilter('ready')}>
            {t('admin.orders.filterReady')}
          </FilterButton>
          <FilterButton active={filter === 'completed'} onClick={() => setFilter('completed')}>
            {t('admin.orders.filterCompleted')}
          </FilterButton>
          <FilterButton active={filter === 'cancelled'} onClick={() => setFilter('cancelled')}>
            {t('admin.orders.filterCancelled')}
          </FilterButton>
        </Filters>
        {actionError && <ActionError role="alert" data-testid="order-action-error">{actionError}</ActionError>}
      </Header>

      {selectedIds.size > 0 && (
        <BulkBar data-testid="bulk-bar">
          <BulkCount data-testid="bulk-selected-count">
            {t('admin.orders.bulkSelected', { count: selectedIds.size })}
          </BulkCount>
          <QuickActionButton
            variant="complete"
            data-testid="bulk-complete-btn"
            disabled={bulkPending}
            aria-busy={bulkPending}
            onClick={() => handleBulkAction('completed')}
          >
            <FiCheck />
            {t('admin.orders.completeSelected')}
          </QuickActionButton>
          <QuickActionButton
            variant="cancel"
            data-testid="bulk-cancel-btn"
            disabled={bulkPending}
            aria-busy={bulkPending}
            onClick={() => handleBulkAction('cancelled')}
          >
            <FiX />
            {t('admin.orders.cancelSelected')}
          </QuickActionButton>
          <ActionButton
            data-testid="bulk-clear-btn"
            disabled={bulkPending}
            onClick={() => setSelectedIds(new Set())}
          >
            {t('admin.orders.clearSelection')}
          </ActionButton>
        </BulkBar>
      )}

      {bulkResult && (
        <BulkResult data-testid="bulk-result" role="status">
          <div>
            {t('admin.orders.bulkResultSummary', {
              updated: bulkResult.updated,
              skipped: bulkResult.skipped,
              failed: bulkResult.failed,
            })}
          </div>
          {bulkResult.results.some(r => r.outcome !== 'updated') && (
            <ul>
              {bulkResult.results
                .filter(r => r.outcome !== 'updated')
                .map(r => (
                  <li key={r.order_id} data-testid={`bulk-result-row-${r.order_id}`}>
                    {t('admin.orders.orderNumberTitle', { number: r.order_number ?? r.order_id })}: {bulkReasonLabel(r.reason)}
                  </li>
                ))}
            </ul>
          )}
        </BulkResult>
      )}

      {orders.length === 0 ? (
        <EmptyState>
          <FiPackage size={48} />
          <h3>{t('admin.orders.emptyTitle')}</h3>
          <p>{t('admin.orders.emptyDescription')}</p>
        </EmptyState>
      ) : (
        <>
          {/* Desktop Table View */}
          <TableContainer>
            <Table>
              <Thead>
                <tr>
                  <Th>
                    <input
                      type="checkbox"
                      ref={setIndeterminate}
                      checked={allSelected}
                      onChange={toggleAll}
                      aria-label={t('admin.orders.selectAll')}
                      data-testid="order-select-all"
                    />
                  </Th>
                  <Th>{t('admin.orders.orderHeader')}</Th>
                  <Th>{t('admin.orders.customerHeader')}</Th>
                  <Th>{t('admin.orders.statusHeader')}</Th>
                  <Th>{t('admin.orders.paymentHeader')}</Th>
                  <Th>{t('admin.orders.amountHeader')}</Th>
                  <Th>{t('admin.orders.pickupHeader')}</Th>
                  <Th>{t('admin.orders.actionsHeader')}</Th>
                </tr>
              </Thead>
              <Tbody>
                {orders.map((order) => (
                  <Tr key={order.order_id}>
                    <Td>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(order.order_id)}
                        onChange={() => toggleOne(order.order_id)}
                        aria-label={t('admin.orders.selectOrder', { number: order.order_number })}
                        data-testid={`order-select-${order.order_id}`}
                      />
                    </Td>
                    <Td>
                      <OrderNumber>#{order.order_number}</OrderNumber>
                      <OrderDate>{formatDate(order.created_at)}</OrderDate>
                    </Td>
                    <Td>
                      <CustomerInfo>
                        <CustomerName>{order.customer_name}</CustomerName>
                        <CustomerContact>{order.customer_phone}</CustomerContact>
                      </CustomerInfo>
                    </Td>
                    <Td>
                      <StatusBadge status={order.status}>
                        {getStatusIcon(order.status)}
                        {getStatusLabel(order.status, t)}
                      </StatusBadge>
                    </Td>
                    <Td>
                      <PaymentBadge status={order.payment_status || 'pending'}>
                        <FiDollarSign size={12} />
                        {formatPaymentStatus(order.payment_status, t)}
                      </PaymentBadge>
                    </Td>
                    <Td>
                      <Amount>{formatCurrency(order.total_amount)}</Amount>
                    </Td>
                    <Td>
                      <PickupInfo>
                        <PickupDate>{formatDate(order.pickup_date)}</PickupDate>
                        <PickupTime>{formatTime(order.pickup_time)}</PickupTime>
                      </PickupInfo>
                    </Td>
                    <Td>
                      <Actions>
                        <ActionButton onClick={() => handleViewDetails(order)}>
                          <FiEye />
                          {t('admin.orders.viewButton')}
                        </ActionButton>
                        {renderQuickActions(order, order.order_id)}
                      </Actions>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>

          {/* Mobile Card View */}
          <MobileContainer>
            <SelectAllRow>
              <input
                type="checkbox"
                ref={setIndeterminate}
                checked={allSelected}
                onChange={toggleAll}
                data-testid="order-select-all-mobile"
              />
              {t('admin.orders.selectAll')}
            </SelectAllRow>
            {orders.map((order) => (
              <OrderCard key={order.order_id}>
                <CardHeader>
                  <SelectCell>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(order.order_id)}
                      onChange={() => toggleOne(order.order_id)}
                      aria-label={t('admin.orders.selectOrder', { number: order.order_number })}
                      data-testid={`order-select-mobile-${order.order_id}`}
                    />
                  </SelectCell>
                  <div style={{ flex: 1 }}>
                    <OrderNumber>{t('admin.orders.orderNumberTitle', { number: order.order_number })}</OrderNumber>
                    <OrderDate>{formatDate(order.created_at)}</OrderDate>
                  </div>
                  <StatusBadge status={order.status}>
                    {getStatusIcon(order.status)}
                    {getStatusLabel(order.status, t)}
                  </StatusBadge>
                </CardHeader>
                <CardBody>
                  <CardRow>
                    <CardLabel>{t('admin.orders.customerHeader')}</CardLabel>
                    <CardValue>{order.customer_name}</CardValue>
                  </CardRow>
                  <CardRow>
                    <CardLabel>{t('admin.orders.phoneLabel')}</CardLabel>
                    <CardValue>{order.customer_phone}</CardValue>
                  </CardRow>
                  <CardRow>
                    <CardLabel>{t('admin.orders.amountHeader')}</CardLabel>
                    <Amount>{formatCurrency(order.total_amount)}</Amount>
                  </CardRow>
                  <CardRow>
                    <CardLabel>{t('admin.orders.paymentHeader')}</CardLabel>
                    <PaymentBadge status={order.payment_status || 'pending'}>
                      <FiDollarSign size={12} />
                      {formatPaymentStatus(order.payment_status, t)}
                    </PaymentBadge>
                  </CardRow>
                  <CardRow>
                    <CardLabel>{t('admin.orders.pickupHeader')}</CardLabel>
                    <CardValue>
                      {t('admin.orders.pickupDateTimeValue', { date: formatDate(order.pickup_date), time: formatTime(order.pickup_time) })}
                    </CardValue>
                  </CardRow>
                </CardBody>
                <ActionButton
                  onClick={() => handleViewDetails(order)}
                  style={{ width: '100%', marginTop: '12px', justifyContent: 'center' }}
                >
                  <FiEye />
                  {t('admin.orders.viewDetailsButton')}
                </ActionButton>
                {!isTerminalStatus(order.status) && (
                  <Actions style={{ marginTop: '8px' }}>
                    {renderQuickActions(order, order.order_id, true)}
                  </Actions>
                )}
              </OrderCard>
            ))}
          </MobileContainer>
        </>
      )}

      <Modal isOpen={!!selectedOrder}>
        {selectedOrder && (
          <ModalContent>
            <ModalHeader>
              <ModalTitle>{t('admin.orders.orderNumberTitle', { number: selectedOrder.order_number })}</ModalTitle>
              <CloseButton onClick={() => { setSelectedOrder(null); setActionError(null); }}>×</CloseButton>
            </ModalHeader>
            {actionError && <ActionError role="alert" data-testid="order-action-error-modal">{actionError}</ActionError>}

            <OrderDetails>
              <DetailSection>
                <DetailTitle>{t('admin.orders.orderInformationTitle')}</DetailTitle>
                <DetailRow>
                  <DetailLabel>{t('admin.orders.orderIdLabel')}</DetailLabel>
                  <DetailValue>{selectedOrder.order_id}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>{t('admin.orders.orderDateLabel')}</DetailLabel>
                  <DetailValue>{formatDate(selectedOrder.created_at)}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>{t('admin.orders.statusColonLabel')}</DetailLabel>
                  <DetailValue>
                    <StatusBadge status={selectedOrder.status}>
                      {getStatusLabel(selectedOrder.status, t)}
                    </StatusBadge>
                  </DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>{t('admin.orders.paymentStatusColonLabel')}</DetailLabel>
                  <DetailValue>
                    <PaymentBadge status={selectedOrder.payment_status || 'pending'}>
                      {formatPaymentStatus(selectedOrder.payment_status, t)}
                    </PaymentBadge>
                  </DetailValue>
                </DetailRow>
              </DetailSection>

              <DetailSection>
                <DetailTitle>{t('admin.orders.customerInformationTitle')}</DetailTitle>
                <DetailRow>
                  <DetailLabel>{t('admin.orders.nameColonLabel')}</DetailLabel>
                  <DetailValue>{selectedOrder.customer_name}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>{t('admin.orders.emailColonLabel')}</DetailLabel>
                  <DetailValue>{selectedOrder.customer_email}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>{t('admin.orders.phoneColonLabel')}</DetailLabel>
                  <DetailValue>{selectedOrder.customer_phone}</DetailValue>
                </DetailRow>
                {selectedOrder.special_instructions && (
                  <DetailRow>
                    <DetailLabel>{t('admin.orders.specialInstructionsLabel')}</DetailLabel>
                    <DetailValue>{selectedOrder.special_instructions}</DetailValue>
                  </DetailRow>
                )}
              </DetailSection>

              <DetailSection>
                <DetailTitle>{t('admin.orders.pickupInformationTitle')}</DetailTitle>
                <DetailRow>
                  <DetailLabel>{t('admin.orders.dateColonLabel')}</DetailLabel>
                  <DetailValue>{formatDate(selectedOrder.pickup_date)}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>{t('admin.orders.timeColonLabel')}</DetailLabel>
                  <DetailValue>{formatTime(selectedOrder.pickup_time)}</DetailValue>
                </DetailRow>
              </DetailSection>

              {selectedOrder.items && selectedOrder.items.length > 0 && (
                <DetailSection>
                  <DetailTitle>{t('admin.orders.orderItemsTitle')}</DetailTitle>
                  <ItemsList>
                    {selectedOrder.items.map((item) => {
                      const optionsStr = formatSelectedOptions(item.selected_options, t);
                      return (
                        <Item key={item.order_item_id}>
                          <div>
                            <div>{item.product_name}</div>
                            <div style={{ fontSize: '12px', color: '#6b7280' }}>
                              {item.size_name ? `${item.size_name} × ` : ''}{item.quantity}
                            </div>
                            {optionsStr && (
                              <div style={{ fontSize: '11px', color: '#6B5E4E', marginTop: '2px' }}>
                                {optionsStr}
                              </div>
                            )}
                            {item.custom_image_url && (
                              <a
                                href={resolveUploadUrl(item.custom_image_url)}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="order-item-photo"
                                title={t('admin.orders.cakePhotoLabel')}
                                style={{ display: 'inline-block', marginTop: '6px' }}
                              >
                                <img
                                  src={resolveUploadUrl(item.custom_image_url)}
                                  alt={t('admin.orders.cakePhotoAlt')}
                                  style={{ width: '64px', height: '64px', objectFit: 'cover', borderRadius: '4px', border: '1px solid #e5e7eb' }}
                                />
                              </a>
                            )}
                          </div>
                          <div style={{ fontWeight: 600 }}>
                            {formatCurrency(item.subtotal)}
                          </div>
                        </Item>
                      );
                    })}
                  </ItemsList>
                  <DetailRow style={{ marginTop: '12px', fontWeight: 600 }}>
                    <DetailLabel>{t('admin.orders.totalColonLabel')}</DetailLabel>
                    <DetailValue>{formatCurrency(selectedOrder.total_amount)}</DetailValue>
                  </DetailRow>
                </DetailSection>
              )}

              {!isTerminalStatus(selectedOrder.status) && (
                <DetailSection>
                  <Actions>{renderQuickActions(selectedOrder, 'modal')}</Actions>
                </DetailSection>
              )}

              <DetailSection>
                <DetailTitle>{t('admin.orders.updateStatusSectionTitle')}</DetailTitle>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <StatusSelect
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value)}
                  >
                    <option value="pending">{t('admin.orders.statusOptionPending')}</option>
                    <option value="confirmed">{t('admin.orders.statusOptionConfirmed')}</option>
                    <option value="in_preparation">{t('admin.orders.statusOptionInPreparation')}</option>
                    <option value="ready_for_pickup">{t('admin.orders.statusOptionReadyForPickup')}</option>
                    <option value="completed">{t('admin.orders.statusOptionCompleted')}</option>
                    <option value="cancelled">{t('admin.orders.statusOptionCancelled')}</option>
                  </StatusSelect>
                  <UpdateButton
                    onClick={handleUpdateStatus}
                    disabled={newStatus === selectedOrder.status}
                  >
                    {t('admin.orders.updateStatusButton')}
                  </UpdateButton>
                </div>
              </DetailSection>
            </OrderDetails>
          </ModalContent>
        )}
      </Modal>
    </Container>
  );
};

export default AdminOrders;